import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { DataContext } from '../../../context/DataContext';
import { getRol, getToken, getUser } from '../../../api/Account';
import { getRecuperacionEfectivaAsync } from '../../../api/Report';
import { getGestoresBySucursalAccessAsync, getSucursalByUserAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { importe, porcentaje } from '../../../helpers/formato';
import ReporteAgrupado from '../../../components/reportes/ReporteAgrupado';

const TODOS = 't';

const COLUMNAS = [
	{ key: 'id', label: '#', align: 'center', valor: (c) => c.id, excel: (c) => c.id },
	{ key: 'fullName', label: 'Cliente', align: 'left', valor: (c) => c.fullName },
	{ key: 'phone', label: 'Telefono', align: 'center', valor: (c) => c.phone },
	{
		key: 'diaAtencion',
		label: 'D.Atencion',
		align: 'center',
		valor: (c) => c.diaAtencion ?? ''
	},
	{
		key: 'cuotasDelPeriodo',
		label: 'Cuotas',
		align: 'center',
		valor: (c) => c.cuotasDelPeriodo,
		excel: (c) => c.cuotasDelPeriodo
	},
	{
		key: 'interesEsperado',
		label: 'Debio Recuperar',
		align: 'right',
		total: true,
		formatoTotal: importe,
		valor: (c) => importe(c.interesEsperado),
		excel: (c) => c.interesEsperado
	},
	{
		key: 'interesRecuperado',
		label: 'Recupero',
		align: 'right',
		total: true,
		formatoTotal: importe,
		valor: (c) => importe(c.interesRecuperado),
		excel: (c) => c.interesRecuperado
	},
	{
		key: 'interesPendiente',
		label: 'Pendiente',
		align: 'right',
		total: true,
		formatoTotal: importe,
		valor: (c) => importe(c.interesPendiente),
		excel: (c) => c.interesPendiente
	},
	{
		// El cumplimiento no se suma: el del grupo se recalcula sobre sus totales.
		key: 'cumplimiento',
		label: 'Cumplimiento',
		align: 'center',
		valor: (c) => porcentaje(c.cumplimiento),
		excel: (c) => c.cumplimiento
	}
];

const RecuperacionEfectiva = () => {
	const [data, setData] = useState([]);
	const [gestorList, setGestorList] = useState([]);
	const [sucursalName, setSucursalName] = useState('Todas');
	const [gestorName, setGestorName] = useState('Todos');

	const { selectedSucursal, selectedGestor, fechaDesde, fechaHasta } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);

			const [reporte, sucursales, gestores] = await Promise.all([
				getRecuperacionEfectivaAsync(token, {
					sucursalId: selectedSucursal === TODOS ? 0 : Number(selectedSucursal),
					gestorId: selectedGestor === TODOS ? 0 : Number(selectedGestor),
					fechaDesde,
					fechaHasta
				}),
				getSucursalByUserAsync(token),
				getGestoresBySucursalAccessAsync(token)
			]);

			setIsLoading(false);

			if (!reporte.statusResponse) {
				manejarError(reporte);
				return;
			}
			setData(reporte.data.result);

			if (gestores.statusResponse) {
				setGestorList(gestores.data.result);
				if (selectedGestor !== TODOS) {
					const ges = gestores.data.result.find(
						(g) => g.id.toString() === selectedGestor
					);
					setGestorName(ges ? ges.fullName : selectedGestor);
				}
			}
			if (selectedSucursal !== TODOS && sucursales.statusResponse) {
				const suc = sucursales.data.result.find(
					(s) => s.id.toString() === selectedSucursal
				);
				setSucursalName(suc ? suc.description : selectedSucursal);
			}
		})();
	}, [
		token,
		selectedSucursal,
		selectedGestor,
		fechaDesde,
		fechaHasta,
		setIsLoading,
		manejarError
	]);

	const nombreGestor = useCallback(
		(fila) => {
			const gestor = gestorList.find((g) => g.id === fila.gestor);
			return gestor ? gestor.fullName : 'Sin gestor';
		},
		[gestorList]
	);

	// Cumplimiento global del periodo, para el encabezado: se calcula sobre los
	// totales y no promediando porcentajes, que daria un numero distinto.
	const totalEsperado = data.reduce((suma, c) => suma + c.interesEsperado, 0);
	const totalRecuperado = data.reduce((suma, c) => suma + c.interesRecuperado, 0);
	const cumplimientoGlobal =
		totalEsperado === 0 ? 0 : (totalRecuperado / totalEsperado) * 100;

	return (
		<ReporteAgrupado
			tituloReporte="Recuperacion Efectiva de Intereses"
			nombreArchivo="RecuperacionEfectiva"
			parametros={[
				{ label: 'Sucursal', valor: sucursalName },
				{ label: 'Gestor', valor: gestorName },
				{
					label: 'Cuotas que vencen entre',
					valor: `${moment(fechaDesde).format('L')} y ${moment(fechaHasta).format('L')}`
				},
				{ label: 'Cumplimiento global', valor: porcentaje(cumplimientoGlobal) }
			]}
			columnas={COLUMNAS}
			data={data}
			agruparPor={nombreGestor}
			etiquetaGrupo="Gestor"
			contexto={{ title, slogan, icon, user, rol }}
		/>
	);
};

export default RecuperacionEfectiva;
